import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { EditionTypeViewStore } from "../../store";
import { EditionTypeView } from "../view/EditionTypeView";
import { EditionTypeUpdateStore } from "../../store";
import { EditionTypeUpdate } from "../update/EditionTypeUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type EditionTypeModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const EditionTypeModalContent: React.FC<EditionTypeModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new EditionTypeUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new EditionTypeViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <EditionTypeView
            editionTypeViewStore={storeInfo.store as EditionTypeViewStore}
          />
        )}
        {storeInfo.type === "update" && (
          <EditionTypeUpdate
            editionTypeUpdateStore={storeInfo.store as EditionTypeUpdateStore}
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
